// ============================================
// Garment Registry — Fashion Plate v2
//
// Maps each garment piece to its context variables
// and possible visual states. Used by the Outfit
// Prompt (Prompt 3) to dress the AI nurse persona.
// ============================================

import type {
  GarmentPiece,
  GarmentIntensity,
  GarmentRegistryEntry,
} from '../../types/fashionPlate';
import type { ContextLayer } from '../../types/patientJourney';

// -------------------------------------------------------
// Registry — 6 garment pieces, 3-5 variants each
// -------------------------------------------------------

export const GARMENT_REGISTRY: GarmentRegistryEntry[] = [
  // =====================
  // SHOES — Temporal / Pace
  // =====================
  {
    piece: 'shoes',
    contextCategory: 'Temporal / Pace',
    variables: ['timeOfDay', 'timeSinceLastInteraction', 'season'],
    variants: [
      {
        label: 'Soft slippers',
        intensity: 'minimal',
        description:
          'Gentle night-time approach. Quiet footsteps, no urgency. ' +
          'Used for late-night crisis moments when the patient is fragile.',
        triggerValues: {
          timeOfDay: ['late_night'],
        },
      },
      {
        label: 'Patient shoes',
        intensity: 'moderate',
        description:
          'Default first-visit pace. Unhurried, warm, careful not to ' +
          'overwhelm. Gives the patient time to settle in.',
        triggerValues: {
          timeSinceLastInteraction: ['first_visit'],
        },
      },
      {
        label: 'Business shoes',
        intensity: 'full',
        description:
          'Proactive morning energy. Confident stride, structured delivery. ' +
          'Matches the patient who arrived motivated and ready to act.',
        triggerValues: {
          timeOfDay: ['morning'],
        },
      },
      {
        label: 'Familiar boots',
        intensity: 'full',
        description:
          'Worn-in comfort for returning patients. References shared history, ' +
          'picks up where we left off. No need to re-introduce.',
        triggerValues: {
          timeSinceLastInteraction: ['weeks', 'months'],
        },
      },
      {
        label: 'Emergency shoes',
        intensity: 'max',
        description:
          'Crisis-speed approach. Immediate triage energy. Skips pleasantries, ' +
          'validates urgency, moves to action fast.',
        triggerValues: {
          timeOfDay: ['late_night', 'evening'],
          timeSinceLastInteraction: ['first_visit', 'months'],
        },
      },
    ],
  },

  // =====================
  // HEART — Emotional / Relational
  // =====================
  {
    piece: 'heart',
    contextCategory: 'Emotional / Relational',
    variables: [
      'baselineStress',
      'recentLifeEvent',
      'partnerStatus',
      'caregiverBurden',
      'socialSupport',
    ],
    variants: [
      {
        label: 'Steady heart',
        intensity: 'minimal',
        description:
          'Calm, even-keel empathy. The patient is stable, no major emotional ' +
          'load. Warm but not overreaching.',
        triggerValues: {
          baselineStress: ['low'],
          recentLifeEvent: ['none'],
        },
      },
      {
        label: 'Warm heart',
        intensity: 'moderate',
        description:
          'Attentive and caring. Moderate stress or light caregiver burden ' +
          'detected. Leans in with genuine concern without crowding.',
        triggerValues: {
          baselineStress: ['moderate'],
          caregiverBurden: ['light'],
          socialSupport: ['moderate'],
        },
      },
      {
        label: 'Glowing heart',
        intensity: 'full',
        description:
          'Full empathetic presence. High stress, recent life event, or ' +
          'isolation detected. Names the difficulty, holds space, validates.',
        triggerValues: {
          baselineStress: ['high'],
          recentLifeEvent: ['divorce', 'job_loss', 'bereavement', 'new_diagnosis'],
          socialSupport: ['isolated'],
        },
      },
      {
        label: 'Crisis heart',
        intensity: 'max',
        description:
          'Maximum empathy mode. Crisis-level stress combined with isolation ' +
          'or heavy caregiving. Every word is chosen to de-escalate and hold.',
        triggerValues: {
          baselineStress: ['crisis'],
          caregiverBurden: ['heavy'],
          socialSupport: ['isolated'],
        },
      },
      {
        label: 'Armored heart',
        intensity: 'minimal',
        description:
          'Respectfully guarded. The skeptical patient who does not want ' +
          'feelings. Data-forward, empathy is implicit not explicit.',
        triggerValues: {
          baselineStress: ['low', 'moderate'],
          socialSupport: ['strong'],
        },
      },
    ],
  },

  // =====================
  // BRAIN — Literacy / Knowledge
  // =====================
  {
    piece: 'brain',
    contextCategory: 'Literacy / Knowledge',
    variables: ['healthKnowledge', 'supplementExperience', 'aiComfort'],
    variants: [
      {
        label: 'Simple brain',
        intensity: 'minimal',
        description:
          'Plain-language mode. Uses everyday analogies, avoids jargon entirely. ' +
          'Explains what a biomarker IS before saying what theirs means.',
        triggerValues: {
          healthKnowledge: ['minimal'],
          supplementExperience: ['none'],
          aiComfort: ['distrustful'],
        },
      },
      {
        label: 'Teaching brain',
        intensity: 'moderate',
        description:
          'Educator mode. Defines terms on first use, builds understanding ' +
          'step by step. Checks comprehension before moving forward.',
        triggerValues: {
          healthKnowledge: ['basic'],
          supplementExperience: ['none', 'some'],
          aiComfort: ['cautious', 'neutral'],
        },
      },
      {
        label: 'Informed brain',
        intensity: 'full',
        description:
          'Peer-level explanation. Uses clinical terms with brief context. ' +
          'References evidence grades. Assumes the patient can handle nuance.',
        triggerValues: {
          healthKnowledge: ['informed'],
          supplementExperience: ['some', 'extensive'],
          aiComfort: ['neutral', 'comfortable'],
        },
      },
      {
        label: 'Citation brain',
        intensity: 'max',
        description:
          'Expert mode. Full citations, evidence grades, mechanism-of-action ' +
          'detail. Speaks as a knowledgeable colleague, not a teacher.',
        triggerValues: {
          healthKnowledge: ['expert'],
          supplementExperience: ['extensive'],
          aiComfort: ['comfortable'],
        },
      },
    ],
  },

  // =====================
  // HAT — Clinical Authority
  // =====================
  {
    piece: 'hat',
    contextCategory: 'Clinical Authority',
    variables: ['healthKnowledge', 'journeyStage', 'adherenceLevel'],
    variants: [
      {
        label: 'Approachable headband',
        intensity: 'minimal',
        description:
          'Minimal clinical authority framing. Positions herself as a helpful ' +
          'friend, not an authority figure. Good for distrustful first visits.',
        triggerValues: {
          healthKnowledge: ['minimal', 'basic'],
          journeyStage: ['first_visit'],
        },
      },
      {
        label: 'Nurse cap',
        intensity: 'moderate',
        description:
          'Standard clinical framing. References Dr. C when appropriate, ' +
          'presents data clearly. Balanced authority and approachability.',
        triggerValues: {
          healthKnowledge: ['basic', 'informed'],
          journeyStage: ['first_visit', 'on_protocol'],
        },
      },
      {
        label: 'Professional cap',
        intensity: 'full',
        description:
          'Full clinical authority. Leads with Dr. C\'s credentials, presents ' +
          'the Captain\'s data with confidence. Used when the patient needs ' +
          'to trust the system.',
        triggerValues: {
          healthKnowledge: ['informed', 'expert'],
          journeyStage: ['on_protocol', 'retesting'],
          adherenceLevel: ['moderate', 'low'],
        },
      },
      {
        label: 'Authority crown',
        intensity: 'max',
        description:
          'Maximum clinical weight. Post-escalation or re-engagement scenarios ' +
          'where trust must be rebuilt through credentialed authority. Every ' +
          'recommendation is backed by name and data.',
        triggerValues: {
          journeyStage: ['post_escalation', 're_engagement'],
          adherenceLevel: ['low'],
        },
      },
    ],
  },

  // =====================
  // DRESS — Financial / Journey
  // =====================
  {
    piece: 'dress',
    contextCategory: 'Financial / Journey',
    variables: ['budgetSensitivity', 'insuranceContext', 'journeyStage', 'adherenceLevel'],
    variants: [
      {
        label: 'Budget-conscious wrap',
        intensity: 'minimal',
        description:
          'Cost-aware tone throughout. Leads with tier 1 options, frames ' +
          'value per dollar. Never makes the patient feel judged for budget.',
        triggerValues: {
          budgetSensitivity: ['high', 'severe'],
          insuranceContext: ['uninsured'],
        },
      },
      {
        label: 'Practical dress',
        intensity: 'moderate',
        description:
          'Balanced presentation. Shows tier 1 and tier 2, explains value ' +
          'clearly. Mentions HSA/FSA eligibility when relevant.',
        triggerValues: {
          budgetSensitivity: ['moderate'],
          insuranceContext: ['insured', 'hsa_fsa_eligible'],
          journeyStage: ['first_visit'],
        },
      },
      {
        label: 'Continuity outfit',
        intensity: 'full',
        description:
          'Journey-aware dressing. References protocol history, progress, ' +
          'and what has changed since last visit. Adapts tier presentation ' +
          'to adherence trajectory.',
        triggerValues: {
          journeyStage: ['on_protocol', 'retesting'],
          adherenceLevel: ['high', 'moderate'],
        },
      },
      {
        label: 'Premium ensemble',
        intensity: 'full',
        description:
          'Full-tier presentation. Patient has low budget sensitivity and is ' +
          'motivated. Shows the complete protocol without price hedging.',
        triggerValues: {
          budgetSensitivity: ['low'],
          insuranceContext: ['insured', 'hsa_fsa_eligible'],
          journeyStage: ['on_protocol', 'retesting'],
        },
      },
      {
        label: 'Recovery gown',
        intensity: 'max',
        description:
          'Re-engagement mode. The patient lapsed or was escalated. Tone ' +
          'is non-judgmental, forward-looking. Rebuilds without blame.',
        triggerValues: {
          journeyStage: ['post_escalation', 're_engagement'],
          adherenceLevel: ['low'],
        },
      },
    ],
  },

  // =====================
  // ACCESSORIES — Behavioral Modifiers
  // =====================
  {
    piece: 'accessories',
    contextCategory: 'Behavioral Modifiers',
    variables: ['motivation', 'cyclePhase', 'season'],
    variants: [
      {
        label: 'Reading glasses',
        intensity: 'moderate',
        description:
          'Evidence-mode accessory. The patient is skeptical or expert-level. ' +
          'Adds precision, sourced claims, and a measured cadence.',
        triggerValues: {
          motivation: ['skeptical'],
        },
      },
      {
        label: 'Warm scarf',
        intensity: 'moderate',
        description:
          'Winter/SAD context accessory. Adds warmth cues, acknowledges ' +
          'seasonal mood shifts, references vitamin D and light exposure.',
        triggerValues: {
          season: ['winter', 'fall'],
        },
      },
      {
        label: 'Wristwatch',
        intensity: 'minimal',
        description:
          'Time-pressed accessory. The patient is proactive and busy. ' +
          'Keeps responses tight, uses bullet points, respects their time.',
        triggerValues: {
          motivation: ['proactive'],
        },
      },
      {
        label: 'Cycle tracker bracelet',
        intensity: 'moderate',
        description:
          'Hormone-cycle-aware accessory. Adjusts supplement timing ' +
          'recommendations and empathy calibration to cycle phase.',
        triggerValues: {
          cyclePhase: ['follicular', 'ovulatory', 'luteal', 'menstrual'],
        },
      },
      {
        label: 'Comfort shawl',
        intensity: 'full',
        description:
          'Desperate/concerned accessory. Maximum gentleness overlay. ' +
          'Slows pacing, softens language, adds reassurance checkpoints.',
        triggerValues: {
          motivation: ['desperate', 'concerned'],
        },
      },
    ],
  },
];

// -------------------------------------------------------
// Resolver — picks the best variant for a given context
// -------------------------------------------------------

/**
 * Resolve the best garment variant for a given piece and context.
 *
 * Scoring: for each variant, count how many of its triggerValues
 * keys match a value present in the provided context. The variant
 * with the most matching trigger keys wins. On a tie, the variant
 * listed first in the registry (i.e. the more specific / earlier
 * defined one) wins. If no triggers match at all, the first
 * variant in the list is returned as the fallback.
 */
export function resolveGarmentLabel(
  piece: GarmentPiece,
  context: Partial<ContextLayer>,
): { label: string; intensity: GarmentIntensity } {
  const entry = GARMENT_REGISTRY.find((e) => e.piece === piece);

  if (!entry || entry.variants.length === 0) {
    return { label: 'default', intensity: 'minimal' };
  }

  let bestVariant = entry.variants[0];
  let bestScore = 0;

  for (const variant of entry.variants) {
    let score = 0;

    for (const [triggerKey, acceptedValues] of Object.entries(variant.triggerValues)) {
      const contextValue = context[triggerKey as keyof ContextLayer];
      if (contextValue !== undefined && acceptedValues.includes(contextValue as string)) {
        score += 1;
      }
    }

    if (score > bestScore) {
      bestScore = score;
      bestVariant = variant;
    }
  }

  return {
    label: bestVariant.label,
    intensity: bestVariant.intensity,
  };
}
